import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { customizeSVG, extractSVGColors } from './svgUtils';

interface IconEditModalProps {
  icon: any; // Alterado para 'any' para aceitar diferentes formatos de ícone
  show: boolean;
  onHide: () => void;
  onSave: (iconData: any) => Promise<void>;
}

interface SVGCustomizations {
  size: number;
  opacity: number;
  strokeWidth: number;
  strokeColor: string;
  fillColor: string;
  backgroundColor: string;
}

export const IconEditModal: React.FC<IconEditModalProps> = ({
  icon,
  show,
  onHide,
  onSave
}) => {
  const [name, setName] = useState('');
  const [customizations, setCustomizations] = useState<SVGCustomizations>({
    size: 32,
    opacity: 1,
    strokeWidth: 1,
    strokeColor: '#000000',
    fillColor: '#000000',
    backgroundColor: 'transparent'
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalSVG, setOriginalSVG] = useState('');
  const [customizedSVG, setCustomizedSVG] = useState('');
  const [showComparison, setShowComparison] = useState(false);
  
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (icon) {
      // Suporta tanto formato antigo (name, svg_content) quanto novo (description, svg)
      setName(icon.name || icon.description || '');
      const svgContent = icon.svg_content || icon.svg || '';
      setOriginalSVG(svgContent);
      setCustomizedSVG(svgContent);
      
      // Extrair cores atuais do SVG
      const svgColors = extractSVGColors(svgContent);
      
      // Determinar tamanho atual do SVG se especificado
      let currentSize = 32;
      const widthMatch = svgContent.match(/width="([^"]+)"/i);
      if (widthMatch && !isNaN(parseFloat(widthMatch[1]))) {
        currentSize = parseFloat(widthMatch[1]);
      }
      
      // Determinar opacidade atual se especificada
      let currentOpacity = 1;
      const opacityMatch = svgContent.match(/opacity="([^"]+)"/i);
      if (opacityMatch && !isNaN(parseFloat(opacityMatch[1]))) {
        currentOpacity = parseFloat(opacityMatch[1]);
      }
      
      // Configurar customizations com as características atuais do ícone
      const currentCustomizations = {
        size: currentSize,
        opacity: currentOpacity,
        strokeWidth: svgColors.strokeWidth,
        strokeColor: svgColors.strokeColor,
        fillColor: svgColors.fillColor,
        backgroundColor: 'transparent'
      };
      setCustomizations(currentCustomizations);
      setShowComparison(false);
    }
  }, [icon]);

  useEffect(() => {
    if (originalSVG) {
      const customized = customizeSVG(originalSVG, {
        size: customizations.size,
        strokeWidth: customizations.strokeWidth,
        strokeColor: customizations.strokeColor,
        fillColor: customizations.fillColor,
        backgroundColor: customizations.backgroundColor
      });
      setCustomizedSVG(customized);
    }
  }, [originalSVG, customizations]);

  const handleCustomizationChange = (key: keyof SVGCustomizations, value: any) => {
    setCustomizations(prev => {
      const newCustomizations = { ...prev, [key]: value };
      
      // Automatically show comparison when user starts customizing
      if (!showComparison && hasChanges(newCustomizations)) {
        setShowComparison(true);
      }
      
      return newCustomizations;
    });
  };

  const hasChanges = (current: SVGCustomizations) => {
    // Extrair cores originais do SVG para comparação
    const originalColors = extractSVGColors(originalSVG);
    
    const defaults = {
      size: 32,
      opacity: 1,
      strokeWidth: originalColors.strokeWidth,
      strokeColor: originalColors.strokeColor,
      fillColor: originalColors.fillColor,
      backgroundColor: 'transparent'
    };
    
    // Comparação mais robusta
    return (
      current.size !== defaults.size ||
      current.opacity !== defaults.opacity ||
      current.strokeWidth !== defaults.strokeWidth ||
      current.strokeColor !== defaults.strokeColor ||
      current.fillColor !== defaults.fillColor ||
      current.backgroundColor !== defaults.backgroundColor
    );
  };

  const getChangeSummary = () => {
    // Extrair cores originais do SVG para comparação
    const originalColors = extractSVGColors(originalSVG);
    
    const defaults = {
      size: 32,
      opacity: 1,
      strokeWidth: originalColors.strokeWidth,
      strokeColor: originalColors.strokeColor,
      fillColor: originalColors.fillColor,
      backgroundColor: 'transparent'
    };
    
    const changes = [];
    
    if (customizations.size !== defaults.size) {
      changes.push(`Tamanho: ${defaults.size}px → ${customizations.size}px`);
    }
    if (customizations.opacity !== defaults.opacity) {
      changes.push(`Opacidade: ${Math.round(defaults.opacity * 100)}% → ${Math.round(customizations.opacity * 100)}%`);
    }
    if (customizations.strokeWidth !== defaults.strokeWidth) {
      changes.push(`Espessura da linha: ${defaults.strokeWidth}px → ${customizations.strokeWidth}px`);
    }
    if (customizations.strokeColor !== defaults.strokeColor) {
      changes.push(
        <span key="stroke">
          Cor da linha: 
          <span style={{ backgroundColor: defaults.strokeColor, width: '16px', height: '16px', display: 'inline-block', marginLeft: '5px', marginRight: '5px', border: '1px solid #ddd' }}></span>
          →
          <span style={{ backgroundColor: customizations.strokeColor, width: '16px', height: '16px', display: 'inline-block', marginLeft: '5px', border: '1px solid #ddd' }}></span>
        </span>
      );
    }
    if (customizations.fillColor !== defaults.fillColor) {
      changes.push(
        <span key="fill">
          Cor de preenchimento: 
          <span style={{ backgroundColor: defaults.fillColor, width: '16px', height: '16px', display: 'inline-block', marginLeft: '5px', marginRight: '5px', border: '1px solid #ddd' }}></span>
          →
          <span style={{ backgroundColor: customizations.fillColor, width: '16px', height: '16px', display: 'inline-block', marginLeft: '5px', border: '1px solid #ddd' }}></span>
        </span>
      );
    }
    if (customizations.backgroundColor !== defaults.backgroundColor) {
      changes.push(
        <span key="bg">
          Cor de fundo: 
          <span style={{ backgroundColor: defaults.backgroundColor === 'transparent' ? '#f8f9fa' : defaults.backgroundColor, width: '16px', height: '16px', display: 'inline-block', marginLeft: '5px', marginRight: '5px', border: '1px solid #ddd' }}></span>
          →
          <span style={{ backgroundColor: customizations.backgroundColor === 'transparent' ? '#f8f9fa' : customizations.backgroundColor, width: '16px', height: '16px', display: 'inline-block', marginLeft: '5px', border: '1px solid #ddd' }}></span>
        </span>
      );
    }
    
    return changes;
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        id: icon.id,
        name: name.trim(),
        svg_content: customizedSVG
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar ícone');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setShowComparison(false);
    onHide();
  };

  const resetCustomizations = () => {
    // Extrair cores originais do SVG para reset
    const originalColors = extractSVGColors(originalSVG);
    
    setCustomizations({
      size: 32,
      opacity: 1,
      strokeWidth: originalColors.strokeWidth,
      strokeColor: originalColors.strokeColor,
      fillColor: originalColors.fillColor,
      backgroundColor: 'transparent'
    });
    setShowComparison(false);
  };

  if (!icon) return null;

  const changeSummary = getChangeSummary();

  return (
    <Modal show={show} onHide={handleClose} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Editar Ícone</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <div className="row">
          {/* Controles de edição */}
          <div className="col-md-6">
            <h5>Propriedades do Ícone</h5>
            
            <Form.Group className="mb-3">
              <Form.Label>Nome</Form.Label>
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome do ícone"
              />
            </Form.Group>

            {/* Painel de Características Atuais */}
            <div className="alert alert-light border mb-4">
              <h6 className="mb-3"><i className="fas fa-info-circle"></i> Características Atuais do Ícone</h6>
              <div className="row small">
                <div className="col-6">
                  <strong>Tamanho:</strong> {customizations.size}px
                </div>
                <div className="col-6">
                  <strong>Opacidade:</strong> {Math.round(customizations.opacity * 100)}%
                </div>
                <div className="col-6 mt-2">
                  <strong>Espessura da linha:</strong> {customizations.strokeWidth}px
                </div>
                <div className="col-6 mt-2">
                  <strong>Cor da linha:</strong> 
                  {customizations.strokeColor === 'transparent' || customizations.strokeColor === 'none' ? (
                    <span className="text-muted ms-1">Transparente</span>
                  ) : (
                    <span className="ms-1">
                      <span 
                        style={{ 
                          backgroundColor: customizations.strokeColor, 
                          width: '12px', 
                          height: '12px', 
                          display: 'inline-block', 
                          border: '1px solid #ddd',
                          marginRight: '4px'
                        }}
                      ></span>
                      {customizations.strokeColor}
                    </span>
                  )}
                </div>
                <div className="col-6 mt-2">
                  <strong>Cor de preenchimento:</strong>
                  {customizations.fillColor === 'transparent' || customizations.fillColor === 'none' ? (
                    <span className="text-muted ms-1">Transparente</span>
                  ) : (
                    <span className="ms-1">
                      <span 
                        style={{ 
                          backgroundColor: customizations.fillColor, 
                          width: '12px', 
                          height: '12px', 
                          display: 'inline-block', 
                          border: '1px solid #ddd',
                          marginRight: '4px'
                        }}
                      ></span>
                      {customizations.fillColor}
                    </span>
                  )}
                </div>
                <div className="col-6 mt-2">
                  <strong>Cor de fundo:</strong>
                  {customizations.backgroundColor === 'transparent' ? (
                    <span className="text-muted ms-1">Transparente</span>
                  ) : (
                    <span className="ms-1">
                      <span 
                        style={{ 
                          backgroundColor: customizations.backgroundColor, 
                          width: '12px', 
                          height: '12px', 
                          display: 'inline-block', 
                          border: '1px solid #ddd',
                          marginRight: '4px'
                        }}
                      ></span>
                      {customizations.backgroundColor}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <h6>Customizações</h6>

            {/* Configurações básicas */}
            <Form.Group className="mb-3">
              <Form.Label>Tamanho: {customizations.size}px</Form.Label>
              <Form.Range
                min="16"
                max="128"
                value={customizations.size}
                onChange={(e) => handleCustomizationChange('size', parseInt(e.target.value))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Opacidade: {Math.round(customizations.opacity * 100)}%</Form.Label>
              <Form.Range
                min="0"
                max="1"
                step="0.1"
                value={customizations.opacity}
                onChange={(e) => handleCustomizationChange('opacity', parseFloat(e.target.value))}
              />
            </Form.Group>

            {/* Configurações de linha */}
            <h6 className="text-muted mt-4 mb-3">Configurações de Linha</h6>
            
            <Form.Group className="mb-3">
              <Form.Label>Espessura da linha: {customizations.strokeWidth}px</Form.Label>
              <Form.Range
                min="0"
                max="10"
                step="0.5"
                value={customizations.strokeWidth}
                onChange={(e) => handleCustomizationChange('strokeWidth', parseFloat(e.target.value))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cor da linha</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  id="transparent-stroke"
                  label="Transparente"
                  checked={customizations.strokeColor === 'transparent'}
                  onChange={(e) => handleCustomizationChange('strokeColor', e.target.checked ? 'transparent' : '#000000')}
                />
                {customizations.strokeColor !== 'transparent' && (
                  <Form.Control
                    type="color"
                    value={customizations.strokeColor}
                    onChange={(e) => handleCustomizationChange('strokeColor', e.target.value)}
                    style={{ width: '60px' }}
                  />
                )}
              </div>
            </Form.Group>

            {/* Configurações de preenchimento */}
            <h6 className="text-muted mt-4 mb-3">Configurações de Preenchimento</h6>
            
            <Form.Group className="mb-3">
              <Form.Label>Cor de preenchimento</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  id="transparent-fill"
                  label="Transparente"
                  checked={customizations.fillColor === 'transparent'}
                  onChange={(e) => handleCustomizationChange('fillColor', e.target.checked ? 'transparent' : '#000000')}
                />
                {customizations.fillColor !== 'transparent' && (
                  <Form.Control
                    type="color"
                    value={customizations.fillColor}
                    onChange={(e) => handleCustomizationChange('fillColor', e.target.value)}
                    style={{ width: '60px' }}
                  />
                )}
              </div>
            </Form.Group>

            {/* Configurações de fundo */}
            <h6 className="text-muted mt-4 mb-3">Configurações de Fundo</h6>
            
            <Form.Group className="mb-3">
              <Form.Label>Cor de fundo</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  id="transparent-bg"
                  label="Transparente"
                  checked={customizations.backgroundColor === 'transparent'}
                  onChange={(e) => handleCustomizationChange('backgroundColor', e.target.checked ? 'transparent' : '#ffffff')}
                />
                {customizations.backgroundColor !== 'transparent' && (
                  <Form.Control
                    type="color"
                    value={customizations.backgroundColor}
                    onChange={(e) => handleCustomizationChange('backgroundColor', e.target.value)}
                    style={{ width: '60px' }}
                  />
                )}
              </div>
            </Form.Group>

            <div className="d-flex gap-2 mb-3">
              <Button variant="outline-secondary" onClick={resetCustomizations}>
                Resetar
              </Button>
              <Button 
                variant="outline-info" 
                onClick={() => setShowComparison(!showComparison)}
              >
                {showComparison ? 'Ocultar' : 'Mostrar'} Comparação
              </Button>
            </div>
          </div>

          {/* Preview */}
          <div className="col-md-6">
            <h5>Preview</h5>
            
            {showComparison && hasChanges(customizations) ? (
              <div>
                {/* Antes e Depois */}
                <div className="row mb-3">
                  <div className="col-6">
                    <h6 className="text-muted">Original</h6>
                    <div 
                      className="border rounded p-3 text-center"
                      style={{ minHeight: '120px', backgroundColor: '#f8f9fa' }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: originalSVG }}
                        style={{ 
                          display: 'inline-block',
                          width: '32px',
                          height: '32px',
                          maxWidth: '32px',
                          maxHeight: '32px',
                          overflow: 'hidden'
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-6">
                    <h6 className="text-primary">Customizado</h6>
                    <div 
                      className="border rounded p-3 text-center"
                      style={{ 
                        minHeight: '120px', 
                        backgroundColor: customizations.backgroundColor === 'transparent' ? '#f8f9fa' : customizations.backgroundColor
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: customizedSVG }}
                        style={{ 
                          display: 'inline-block',
                          width: `${customizations.size}px`,
                          height: `${customizations.size}px`,
                          maxWidth: `${customizations.size}px`,
                          maxHeight: `${customizations.size}px`,
                          opacity: customizations.opacity,
                          overflow: 'hidden'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Resumo das mudanças */}
                {changeSummary.length > 0 && (
                  <div className="alert alert-info">
                    <h6><i className="fas fa-list"></i> Mudanças aplicadas:</h6>
                    <ul className="mb-0">
                      {changeSummary.map((change, index) => (
                        <li key={index} style={{ fontSize: '0.9em' }}>
                          {change}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div 
                className="border rounded p-3 text-center"
                style={{ 
                  minHeight: '200px', 
                  backgroundColor: customizations.backgroundColor === 'transparent' ? '#f8f9fa' : customizations.backgroundColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
                ref={previewRef}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: customizedSVG }}
                  style={{ 
                    display: 'inline-block',
                    width: `${customizations.size}px`,
                    height: `${customizations.size}px`,
                    maxWidth: `${Math.min(customizations.size, 150)}px`,
                    maxHeight: `${Math.min(customizations.size, 150)}px`,
                    opacity: customizations.opacity,
                    overflow: 'hidden'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};